import IconGradient from "components/EvtKit/icons/IconGradient"
import IconPalette from "components/EvtKit/icons/IconPalette"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"
import BodyText from "components/Typography/BodyText"
import Caption from "components/Typography/Caption"
import React, { memo, useCallback } from "react"
import { ColorPicker, ColorService, IColor } from "react-color-palette"
import styled from "styled-components"
import "react-color-palette/css"
import tinycolor from "tinycolor2"
import { removeNonHexCharacters } from "constants/product"

const ColorFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid transparent;
  background-image: linear-gradient(white, white),
    linear-gradient(90deg, #ff0078, #ff00db, #9f00ff, #070bff, #00fff3, #27ff00, #f6f300, #ff0000); /* Gradiente na borda */
  background-origin: border-box;
  background-clip: padding-box, border-box;
`

const ColorPickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  margin-top: 8px;
`

const ColorPickerWrapper = styled.div`
  width: 100%;

  .rcp {
    border-radius: 8px 8px 0px 0px;
  }

  .rcp-saturation {
    border-radius: 8px 8px 0px 0px;
  }

  .rcp-hue-cursor,
  .rcp-saturation-cursor {
    width: 12px;
    height: 12px;
  }

  .rcp-hue-cursor {
    transform: translate(-6px, 0.5px);
  }

  .rcp-saturation-cursor {
    transform: translate(-6px, -4px);
  }

  .rcp-body {
    padding: 8px 0px 0px 0px;
  }

  .rcp-root {
    --rcp-background-color: black;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const GradientContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const InputContainer = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.palette.black};
  height: 36px;
  border-radius: 0px 0px 4px 4px;
  gap: 8px;
  padding: 8px 12px 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const GradientSwitchContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: fit-content;
  height: 40px;
  padding: 8px 12px 8px 8px;
  position: relative;
`

const GradientIconContainer = styled.div`
  display: flex;
  align-items: center;
`

const InputColor = styled.input`
  width: 60px;
  height: 22px;
  font-size: 10px;
  line-height: 12px;
  border-radius: 4px;
  outline: none;
  color: ${(props) => props.theme.palette.secondary_dark};
  padding-left: 6px;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
`

interface BannerColorFilterProps {
  color: IColor
  setColor: React.Dispatch<React.SetStateAction<IColor>>
  colorSecondary: IColor
  setColorSecondary: React.Dispatch<React.SetStateAction<IColor>>
  bannerInfoForm: App.BannerInfoForm
  handleBannerInfoFormChange: (key: keyof App.BannerInfoForm, value: string | boolean) => void
}

const BannerColorFilter: React.FC<BannerColorFilterProps> = (props: BannerColorFilterProps) => {
  const { color, bannerInfoForm, handleBannerInfoFormChange, setColor, colorSecondary, setColorSecondary } = props

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(removeNonHexCharacters, "")

      const idIsHex = e.target.id === "hex"

      const fieldId = idIsHex ? "hex" : "hexSecondary"

      handleBannerInfoFormChange(fieldId, `#${value}`)

      if (value.length !== 4) {
        const newColor = tinycolor(`#${value}`)

        if (newColor.isValid()) {
          if (fieldId === "hex") {
            setColor(ColorService.convert("hex", `#${value}`))

            return
          }

          setColorSecondary(ColorService.convert("hex", `#${value}`))
        }
      }
    },
    [handleBannerInfoFormChange, setColor]
  )

  const handlePickerOneChange = useCallback((color: IColor) => {
    handleBannerInfoFormChange("hex", color.hex)
    setColor(color)
  }, [])

  const handlePickerTwoChange = useCallback((color: IColor) => {
    handleBannerInfoFormChange("hexSecondary", color.hex)
    setColorSecondary(color)
  }, [])

  const handleGradientChange = useCallback(() => {
    handleBannerInfoFormChange("isGradient", !bannerInfoForm.isGradient)
  }, [bannerInfoForm.isGradient])

  return (
    <ColorFilterContainer>
      <HeaderContainer>
        <TitleContainer>
          <IconPalette />
          <BodyText size="semi-medium" weight="semi-bold" color="secondary-medium">
            Temas
          </BodyText>
        </TitleContainer>
        <GradientContainer>
          <GradientSwitchContainer>
            <SwitchV2 onChange={handleGradientChange} value={bannerInfoForm.isGradient} variant="small" />
            <GradientIconContainer>
              <IconGradient />
              <Caption size="large" weight="semi-bold" color="secondary-medium">
                Gradiente
              </Caption>
            </GradientIconContainer>
          </GradientSwitchContainer>
        </GradientContainer>
      </HeaderContainer>
      <Caption size="large" color="secondary-dark">
        Escolha uma <b>cor base</b> ou <b>gradiente</b> para gerarmos um tema especial para o seu produto. A cor
        selecionada será aplicada tanto no banner quanto no e-mail.
      </Caption>
      <ColorPickerContainer>
        <ColorPickerWrapper>
          <ColorPicker color={color} onChange={handlePickerOneChange} height={84} hideAlpha hideInput />
          <InputContainer>
            <InputColor
              id="hex"
              value={bannerInfoForm.hex}
              onChange={handleHexChange}
              placeholder="#FFFFFF"
              maxLength={7}
            />
          </InputContainer>
        </ColorPickerWrapper>
        {bannerInfoForm.isGradient && (
          <ColorPickerWrapper>
            <ColorPicker color={colorSecondary} onChange={handlePickerTwoChange} height={84} hideAlpha hideInput />
            <InputContainer>
              <InputColor
                id="hexSecondary"
                value={bannerInfoForm.hexSecondary}
                onChange={handleHexChange}
                placeholder="#FFFFFF"
                maxLength={7}
              />
            </InputContainer>
          </ColorPickerWrapper>
        )}
      </ColorPickerContainer>
    </ColorFilterContainer>
  )
}

export default memo(BannerColorFilter)
